import Document from "@/models/SvcModels/Document";
import Tag from "@/models/Tag";
import { imageExtensions } from "@/constants/document";
import omit from "lodash/omit";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useMissionStore } from "@/store/modules/mission";
import { ProjectDocumentsApi, ProjectFilesApi, TagsApi } from "@socotec.io/document-sdk";
import { documentConfig } from "@/setup/openApiConfig";
import { createStoredDocument, storeFile } from "@/api/documentUploadClient";

const DocumentRepo = useRepo(Document);
const TagRepo = useRepo(Tag);

const projectDocumentAPi = new ProjectDocumentsApi(documentConfig);
const projectFileApi = new ProjectFilesApi(documentConfig);
const tagsApi = new TagsApi(documentConfig);

export const useDocumentStore = defineStore("document", {
  state: () => ({
    documentSelected: [],
    documentsCount: 0,
    documentTablePageSize: 10,
  }),

  actions: {
    processDocumentsMapping(documents) {
      const processedDocs = Array.isArray(documents) ? documents : [documents];
      for (const doc of processedDocs) {
        if (doc.uuid && doc.documentCustomUuid) {
          doc.uuid = doc.documentCustomUuid;
        }
      }
      return documents;
    },
    setSelectedDocument(documents) {
      this.documentSelected = documents;
    },

    getRelatedObjectPk() {
      const currentMission = useMissionStore().currentMission;
      return currentMission?.uuid;
    },

    getFallbackRelatedObjectPk() {
      const currentMission = useMissionStore().currentMission;
      return currentMission?.importedFrom;
    },

    async withFallback(apiCall) {
      try {
        return await apiCall(this.getRelatedObjectPk());
      } catch (error) {
        const fallback = this.getFallbackRelatedObjectPk();
        if (error?.response?.status === 404 && fallback) {
          return await apiCall(fallback);
        }
        throw error;
      }
    },

    async getDocumentUrlAndName(uuid, compressed) {
      return this.withFallback(async (relatedObjectPk) => {
        const params = { uuid, relatedObjectPk };
        return compressed
          ? await projectFileApi.retrieveCompressed({
              ...params,
              compression: "report",
            })
          : await projectFileApi.retrieve(params);
      });
    },

    async fetchDocumentByUuid(uuid) {
      const response = await this.withFallback(async (relatedObjectPk) => {
        return await projectDocumentAPi.retrieve({ uuid, relatedObjectPk });
      });
      this.processDocumentsMapping(response);
      DocumentRepo.save(response);
      return response;
    },

    // N.K 09/09/26 Les affaires importées d'une ancienne offre ont leurs documents
    // rattachés à l'uuid de l'offre : une liste vide répond 200, donc `withFallback`
    // ne bascule jamais dessus.
    async fetchDocumentsByUuids(uuids) {
      if (!uuids?.length) return [];
      const relatedObjectPks = [
        ...new Set([this.getRelatedObjectPk(), this.getFallbackRelatedObjectPk()].filter(Boolean)),
      ];
      const documents = [];
      let missingUuids = [...new Set(uuids)];
      for (const relatedObjectPk of relatedObjectPks) {
        if (!missingUuids.length) break;
        const response = await projectDocumentAPi.list({
          relatedObjectPk,
          uuid: missingUuids,
          pageSize: missingUuids.length,
        });
        const results = response.results || [];
        this.processDocumentsMapping(results);
        documents.push(...results);
        const foundUuids = new Set(documents.map((doc) => doc.uuid));
        missingUuids = missingUuids.filter((uuid) => !foundUuids.has(uuid));
      }
      DocumentRepo.save(documents);
      return documents;
    },

    async fetchDocuments({ filters, pagination }) {
      DocumentRepo.flush();
      const response = await this.withFallback(async (relatedObjectPk) => {
        const query = { ...filters, relatedObjectPk };
        if (pagination) {
          query.pageSize = pagination.pageSize || pagination.page_size;
          query.page = pagination.page;
        }
        return await projectDocumentAPi.list(query);
      });
      if (response.results) {
        this.processDocumentsMapping(response.results);
      }
      this.setDocumentCount(response.count);
      return DocumentRepo.save(response.results);
    },

    async fetchDocumentsAndFileUrl({ metadata, compressed }) {
      const count = await this.fetchDocuments(metadata);

      for (const img of this.getImages) {
        const { url } = await this.getDocumentUrlAndName(img.uuid, compressed);
        img.keyFileUrl = url;
        DocumentRepo.save(img);
      }

      return count;
    },

    async fetchFileUrls(uuids) {
      return this.withFallback(async (relatedObjectPk) => {
        const response = await projectFileApi.list({
          relatedObjectPk,
          uuids,
          pageSize: uuids.length,
        });
        return response.results || [];
      });
    },

    async fetchDocumentTags() {
      const response = await tagsApi.list({
        relatedObjectPk: useMissionStore().currentMission.uuid,
      });
      const tags = TagRepo.save(response.results);

      return tags.tag;
    },

    async updateDocument({ form }) {
      if (form.datetime && typeof form.datetime === "string") {
        form.datetime = new Date(form.datetime);
      }
      const response = await projectDocumentAPi.partialUpdate({
        uuid: form.uuid,
        relatedObjectPk: useMissionStore().currentMission.uuid,
        patchedDocumentUpdate: omit(form, [
          "origin",
          "originId",
          "gedProjectId",
          "bimdataOriginId",
          "updateOriginId",
        ]),
      });
      this.processDocumentsMapping(response);
      DocumentRepo.save(response);
    },

    async removeDocument(uuid) {
      await projectDocumentAPi.destroy({
        uuid,
        relatedObjectPk: useMissionStore().currentMission.uuid,
      });
      return DocumentRepo.destroy(uuid);
    },

    async createDocument({ form, file, uploaded }) {
      if (form.datetime && typeof form.datetime === "string") {
        form.datetime = new Date(form.datetime);
      }

      const missionStore = useMissionStore();
      const project = missionStore.currentMission;

      if (!project.uuid) {
        throw new Error("No mission loaded, cannot create a document");
      }

      const projectUuid = project.uuid.toString();

      form.servicesRelatedName = [import.meta.env.VITE_APP_SSP_SERVICE_ID];

      if (file || uploaded) {
        const stored = uploaded ?? (await storeFile({ file, configuration: documentConfig }));
        const response = await createStoredDocument({
          stored,
          form,
          relatedObjectId: projectUuid,
          configuration: documentConfig,
        });
        this.processDocumentsMapping(response);
        DocumentRepo.save(response);
        return response;
      }

      if (form.originId == null) form.originId = "";

      try {
        const response = await projectDocumentAPi.create({
          relatedObjectPk: projectUuid,
          documentCreate: omit(form, ["uuid"]),
        });
        this.processDocumentsMapping(response);
        DocumentRepo.save(response);
        return response;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },

    async updateMultipleDocs({ data, documentIds }) {
      const documents = documentIds.map((documentId) => ({
        uuid: documentId,
        datetime: data.datetime,
        source: data.source,
        tags: data.tags || [],
      }));

      const response = await projectDocumentAPi.bulkUpdate({
        relatedObjectPk: useMissionStore().currentMission.uuid,
        documentBulkUpdate: documents,
      });
      if (response.results) {
        this.processDocumentsMapping(response.results);
      }
      DocumentRepo.save(response.results);
    },

    resetTags() {
      TagRepo.destroy();
    },

    incrementDocumentCount() {
      this.documentsCount++;
    },

    decreaseDocumentCount() {
      this.documentsCount--;
    },

    setDocumentCount(count) {
      this.documentsCount = count;
    },
  },

  getters: {
    getDocuments: (state) => {
      return DocumentRepo.orderBy("createdAt", "desc").limit(state.documentTablePageSize).get();
    },
    getImages: () => {
      return DocumentRepo.orderBy("createdAt", "desc")
        .where((doc) => {
          return imageExtensions.some((ext) => doc.name.includes(ext));
        })
        .get();
    },
    getDocumentsByType: () => (type) => {
      return DocumentRepo.orderBy("createdAt", "desc").where("docType", type).get();
    },
    getDocumentByUuid: () => (uuid) => {
      return DocumentRepo.where("uuid", uuid).first();
    },
    getSelectedDocuments: (state) => {
      return state.documentSelected;
    },
    getDocumentsCount: (state) => {
      return state.documentsCount;
    },
    getDocumentTablePageSize: (state) => {
      return state.documentTablePageSize;
    },
    getTagsNames: () => {
      const tags = TagRepo.all();
      return tags.map((obj) => obj.name);
    },
  },
});
